import { getPlatform } from "@/lib/platform";
import { i18n, SUPPORTED_LOCALES, type SupportedLocale } from "@/i18n";
import { ApiError, SchemaError, rawFetch, detectClientTz } from "./client";
import { SessionEnvelopeSchema, type SessionEnvelope } from "@/store/session";
import { DEVICE_ID_KEY, REFRESH_TOKEN_KEY } from "@/lib/storageKeys";

export { REFRESH_TOKEN_KEY, DEVICE_ID_KEY };

const SUPPORTED_LOCALE_SET = new Set<string>(SUPPORTED_LOCALES);

/** DUE-158: current i18next language narrowed to the 2-letter prefix the
 * backend's `locale` field expects. Mirrors `i18n/detector.ts`'s
 * `normalize()`. Falls back to "en" when i18next hasn't picked a
 * supported language yet (e.g. pre-`initI18n()`). */
function currentLocale(): SupportedLocale {
  const prefix = (i18n.language ?? "").slice(0, 2).toLowerCase();
  return SUPPORTED_LOCALE_SET.has(prefix) ? (prefix as SupportedLocale) : "en";
}

function parseEnvelope(body: unknown): SessionEnvelope {
  const parsed = SessionEnvelopeSchema.safeParse(body);
  if (!parsed.success) {
    throw new SchemaError(parsed.error.issues);
  }
  return parsed.data;
}

async function persistRefreshToken(
  refreshToken: SessionEnvelope["refresh_token"],
): Promise<void> {
  // Both auth endpoints are documented to always return a rotated token;
  // the type-narrowing guard is defense against a schema that allows
  // null/undefined, not an expected runtime path.
  if (typeof refreshToken === "string") {
    await getPlatform().secureStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

/**
 * DUE-158 / DUE-318: iOS and the browser have no Telegram `initData` to
 * reauthenticate with, so both use a refresh-token -> device-guest fallback
 * against the subproject-A endpoints (`POST /api/auth/refresh`,
 * `POST /api/auth/device`).
 *
 * - A stored refresh token is tried first and rotated on success.
 * - A 401 on refresh means the token is dead: it's dropped and the flow
 *   falls through to the device-guest identity.
 * - Any other rawFetch rejection (network error) is rethrown as-is rather
 *   than falling through — a flaky connection must not mint a second guest
 *   identity. The caller (`ensureFreshJwt`/`request()`) already retries.
 * - The device id is minted and persisted *before* the network call so a
 *   crashed/interrupted request can't cause a second UUID on retry.
 */
export async function deviceAuthenticate(): Promise<SessionEnvelope> {
  const { secureStorage } = getPlatform();

  const storedRefreshToken = await secureStorage.getItem(REFRESH_TOKEN_KEY);
  if (storedRefreshToken) {
    const { status, ok, body } = await rawFetch("/api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: storedRefreshToken }),
    });
    if (ok) {
      const envelope = parseEnvelope(body);
      await persistRefreshToken(envelope.refresh_token);
      return envelope;
    }
    if (status === 401) {
      await secureStorage.removeItem(REFRESH_TOKEN_KEY);
    } else {
      throw new ApiError(status, body);
    }
  }

  let deviceId = await secureStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    await secureStorage.setItem(DEVICE_ID_KEY, deviceId);
  }

  const requestBody: {
    device_id: string;
    locale: SupportedLocale;
    client_tz?: string;
    platform: "ios" | "web";
  } = {
    device_id: deviceId,
    locale: currentLocale(),
    // DUE-318: labels users.origin_platform; the backend defaults to ios
    // for builds that predate the field.
    platform: getPlatform().name === "web" ? "web" : "ios",
  };
  const tz = detectClientTz();
  if (tz) requestBody.client_tz = tz;

  const { status, ok, body } = await rawFetch("/api/auth/device", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });
  if (!ok) {
    throw new ApiError(status, body);
  }
  const envelope = parseEnvelope(body);
  await persistRefreshToken(envelope.refresh_token);
  return envelope;
}

/**
 * Final-review fix (iOS account deletion): wipes both Keychain-backed
 * identity keys after a successful account delete.
 *
 * DUE-196: no longer the whole story — `useDeleteAccount`'s `onSuccess`
 * (`api/me.ts`) now runs `clearClientData("account_deletion")`, which wipes
 * these two keys plus every other persisted key, on every platform. This
 * function stays as ProfilePage's iOS-branch call and is idempotent with
 * that wipe; it can be dropped when that call site is next touched.
 *
 * Why both keys, not just the refresh token: the backend's
 * get_by_device_id lookup filters `deleted_at IS NULL`, and the unique
 * constraint on `device_id` is a partial index over live rows only — so a
 * stale `device_id` pointing at the now-tombstoned user would still work
 * fine technically (re-registering it mints a brand-new user row with the
 * same device_id, no collision). Clearing it isn't a correctness fix, it's
 * a UX one: it guarantees the next app launch mints a completely fresh
 * identity from a clean slate rather than quietly re-linking to whatever
 * device fingerprint the deleted account used, which is the explicit,
 * unambiguous "fresh start" this flow promises the user.
 */
export async function clearDeviceCredentials(): Promise<void> {
  const { secureStorage } = getPlatform();
  await secureStorage.removeItem(REFRESH_TOKEN_KEY);
  await secureStorage.removeItem(DEVICE_ID_KEY);
}
